import {useEffect, useCallback} from 'react';
import {useResultModal} from './useResultModal';
import {useUserInfo} from '@/utils/state-hooks';
import {toPriceStr} from '@components/utils';
import {LeaderboardType} from '@/common-pages/leaderboard/config';

type GetYesterdayRewardFunction = () => Promise<{
  rank?: number;
  reward?: number;
  inviteScore?: number;
  inviteCount?: number;
  hasReward: boolean;
}>;

/**
 * 通用的昨日奖励弹窗 hook
 * 每个用户每天只显示一次
 */
export function useYesterdayRewardModal(
  getYesterdayRewardFn: GetYesterdayRewardFunction,
  type: LeaderboardType,
) {
  const {openModal} = useResultModal();
  const userInfo = useUserInfo();

  // 检查今日是否已经显示过弹窗
  const hasShownModalToday = useCallback(() => {
    if (!userInfo?.userId) {
      return false;
    }

    const today = new Date().toDateString();
    const storageKey = `rewardModalLastShown_${type}_${userInfo.userId}`;
    const lastShownDate = localStorage.getItem(storageKey);
    return lastShownDate === today;
  }, [type, userInfo?.userId]);

  // 设置今日已显示弹窗标记
  const markModalShownToday = useCallback(() => {
    if (!userInfo?.userId) {
      return;
    }

    const today = new Date().toDateString();
    const storageKey = `rewardModalLastShown_${type}_${userInfo.userId}`;
    localStorage.setItem(storageKey, today);
  }, [type, userInfo?.userId]);

  useEffect(() => {
    // 如果用户信息还没加载或今天已经显示过弹窗，就不显示
    if (!userInfo?.userId || hasShownModalToday()) {
      return;
    }

    getYesterdayRewardFn().then(res => {
      // 标记今日已显示
      markModalShownToday();
      openModal({
        rank: res.rank || 0,
        hasReward: res.hasReward,
        reward: res.reward
          ? toPriceStr(res.reward, {
              suffixUnit: 'K',
              overPrice: 10000,
              showCurrency: true,
            })
          : 0,
        inviteCount: res.inviteCount || 0,
        inviteScore: res.inviteScore || 0,
      });
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userInfo?.userId, hasShownModalToday, markModalShownToday]);
}
